<!-- LYP 待支付页：展示订单信息，向 WebView 发送 orderId 唤起 App 支付模块 -->
<template>
    <div class="lypnotice">
        <div class="card">
            <div class="status">
                <div class="status-icon" aria-hidden="true">
                    <svg viewBox="0 0 48 48" width="48" height="48" fill="none">
                        <circle cx="24" cy="24" r="22" stroke="currentColor" stroke-width="3"/>
                        <path d="M24 14v12l8 5" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <div class="status-badge">待支付</div>
                <h1 class="status-title">订单待支付</h1>
                <p class="status-desc">请确认金额后，使用 App 支付完成付款</p>
            </div>

            <div class="amount-block">
                <span class="amount-label">应付金额</span>
                <div class="amount-value">
                    <span class="currency">¥</span>
                    <span class="num">{{ displayAmount }}</span>
                </div>
            </div>

            <ul class="info-list">
                <li class="info-item">
                    <span class="label">订单号</span>
                    <span class="value mono">{{ orderId || '—' }}</span>
                </li>
                <li class="info-item" v-if="userId">
                    <span class="label">用户 ID</span>
                    <span class="value">{{ maskedUserId }}</span>
                </li>
                <li class="info-item" v-if="remarkText">
                    <span class="label">备注</span>
                    <span class="value">{{ remarkText }}</span>
                </li>
            </ul>

            <button
                type="button"
                class="pay-btn"
                :disabled="!orderId || paying"
                @click="callAppPay"
            >
                {{ paying ? '正在唤起支付…' : '立即支付' }}
            </button>
            <p class="hint" v-if="payHint">{{ payHint }}</p>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'

export default {
    setup() {
        const { proxy } = getCurrentInstance()
        const paying = ref(false)
        const payHint = ref('')

        const readParam = (name) => {
            const fromHash = proxy.getHashParam(name)
            if (fromHash != null && fromHash !== '') {
                try {
                    return decodeURIComponent(fromHash)
                } catch (e) {
                    return fromHash
                }
            }
            const search = new URLSearchParams(window.location.search)
            const fromSearch = search.get(name)
            if (fromSearch != null && fromSearch !== '') {
                return fromSearch
            }
            const routeQuery = proxy.$route && proxy.$route.query
                ? proxy.$route.query[name]
                : null
            if (routeQuery != null && routeQuery !== '') {
                return Array.isArray(routeQuery) ? routeQuery[0] : String(routeQuery)
            }
            return ''
        }

        const orderId = ref(readParam('orderId') || '')
        const payAmount = ref(readParam('payAmount') || '')
        const userId = ref(readParam('userId') || '')
        const interfaceTypeCode = ref(readParam('interfaceTypeCode') || '')
        const remark = ref(readParam('remark') || '')

        const displayAmount = computed(() => {
            const n = Number(payAmount.value)
            if (!Number.isFinite(n)) {
                return payAmount.value || '0.00'
            }
            return n.toFixed(2)
        })

        const remarkText = computed(() => {
            const r = (remark.value || '').trim()
            if (!r || r.toLowerCase() === 'null' || r === 'undefined') {
                return ''
            }
            return r
        })

        /** 用户 ID 脱敏：保留首尾，中间用 * 替代 */
        const maskedUserId = computed(() => {
            const id = String(userId.value || '')
            if (!id) return ''
            if (id.length <= 2) return '*'.repeat(id.length)
            if (id.length <= 4) return id[0] + '*'.repeat(id.length - 2) + id[id.length - 1]
            return id.slice(0, 2) + '*'.repeat(Math.min(id.length - 4, 4)) + id.slice(-2)
        })

        /** 向原生 WebView 发送支付指令，唤起 App 支付模块 */
        const callAppPay = () => {
            if (!orderId.value) {
                payHint.value = '缺少订单号，无法发起支付'
                return
            }

            // 通知 App：待支付，携带 orderId 打开支付模块
            const message = JSON.stringify({
                action: 'pay',
                status: 'pending',
                orderId: orderId.value,
                payAmount: payAmount.value,
                userId: userId.value,
                interfaceTypeCode: interfaceTypeCode.value,
                remark: remark.value,
            })

            paying.value = true
            payHint.value = ''

            try {
                let sent = false

                if (typeof lypnotice !== 'undefined' && lypnotice && typeof lypnotice.postMessage === 'function') {
                    lypnotice.postMessage(message)
                    sent = true
                } else if (
                    window.lypnotice &&
                    typeof window.lypnotice.postMessage === 'function'
                ) {
                    window.lypnotice.postMessage(message)
                    sent = true
                } else if (
                    window.webkit &&
                    window.webkit.messageHandlers &&
                    window.webkit.messageHandlers.lypnotice
                ) {
                    window.webkit.messageHandlers.lypnotice.postMessage(message)
                    sent = true
                } else if (
                    window.ReactNativeWebView &&
                    typeof window.ReactNativeWebView.postMessage === 'function'
                ) {
                    window.ReactNativeWebView.postMessage(message)
                    sent = true
                }

                if (sent) {
                    payHint.value = '已唤起 App 支付，请在支付页完成付款'
                } else {
                    payHint.value = '未检测到 App 环境，请在 App 内打开本页面'
                    console.warn('[lypnotice] WebView channel not found', message)
                }
            } catch (err) {
                payHint.value = '唤起支付失败，请重试'
                console.error('[lypnotice] postMessage failed', err)
            } finally {
                setTimeout(() => {
                    paying.value = false
                }, 800)
            }
        }

        onMounted(() => {
            document.title = '待支付'
            // 进入页面自动通知 App，携带 orderId 唤起支付模块
            if (orderId.value) {
                callAppPay()
            } else {
                payHint.value = '未获取到订单号'
            }
        })

        return {
            orderId,
            payAmount,
            userId,
            interfaceTypeCode,
            displayAmount,
            remarkText,
            maskedUserId,
            paying,
            payHint,
            callAppPay,
        }
    },
}
</script>

<style scoped>
.lypnotice {
    --ink: #1c1917;
    --muted: #78716c;
    --line: #e7e5e4;
    --paper: #fffefb;
    --teal: #0f766e;
    --teal-dark: #115e59;
    --amber: #d97706;
    --wash: #fffbeb;
    min-height: 100vh;
    box-sizing: border-box;
    padding: 28px 16px 40px;
    background:
        radial-gradient(600px 280px at 10% -10%, rgba(217, 119, 6, 0.14), transparent 60%),
        radial-gradient(520px 240px at 100% 0%, rgba(15, 118, 110, 0.1), transparent 55%),
        linear-gradient(165deg, #fdf8f3 0%, #fff7ed 45%, #f5f0e8 100%);
    font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
    display: flex;
    align-items: flex-start;
    justify-content: center;
}

.card {
    width: min(420px, 100%);
    background: var(--paper);
    border-radius: 6px 22px 6px 22px;
    border: 1px solid rgba(28, 25, 23, 0.08);
    box-shadow:
        10px 10px 0 rgba(217, 119, 6, 0.12),
        0 20px 40px rgba(28, 25, 23, 0.06);
    padding: 28px 22px 24px;
    box-sizing: border-box;
}

.status {
    text-align: center;
    margin-bottom: 22px;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.status-badge {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: var(--amber);
    background: rgba(217, 119, 6, 0.12);
    border: 1px solid rgba(217, 119, 6, 0.35);
    border-radius: 999px;
    padding: 4px 12px;
    margin-bottom: 12px;
}

.status-icon {
    color: var(--amber);
    display: inline-flex;
    margin-bottom: 12px;
}

.status-title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: var(--ink);
    letter-spacing: 0.04em;
}

.status-desc {
    margin: 8px 0 0;
    font-size: 13px;
    color: var(--muted);
    line-height: 1.5;
}

.amount-block {
    background: var(--wash);
    border-radius: 4px 16px 4px 16px;
    border: 1px solid rgba(217, 119, 6, 0.22);
    padding: 18px 16px;
    text-align: center;
    margin-bottom: 18px;
}

.amount-label {
    display: block;
    font-size: 12px;
    color: var(--muted);
    letter-spacing: 0.12em;
    margin-bottom: 6px;
}

.amount-value {
    color: var(--amber);
    font-weight: 700;
    line-height: 1;
}

.amount-value .currency {
    font-size: 18px;
    margin-right: 4px;
    vertical-align: 6px;
}

.amount-value .num {
    font-size: 36px;
    letter-spacing: 0.02em;
    font-variant-numeric: tabular-nums;
}

.info-list {
    list-style: none;
    margin: 0 0 22px;
    padding: 0;
    border-top: 1px dashed var(--line);
}

.info-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 2px;
    border-bottom: 1px dashed var(--line);
    font-size: 14px;
}

.info-item .label {
    color: var(--muted);
    flex-shrink: 0;
}

.info-item .value {
    color: var(--ink);
    text-align: right;
    word-break: break-all;
    font-weight: 500;
}

.info-item .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px;
}

.pay-btn {
    width: 100%;
    height: 48px;
    border: none;
    border-radius: 2px 999px 2px 999px;
    background: var(--teal);
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.1em;
    box-shadow: 0 4px 0 var(--teal-dark);
    cursor: pointer;
    transition: transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease;
}

.pay-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.pay-btn:not(:disabled):active {
    transform: translateY(2px);
    box-shadow: 0 1px 0 var(--teal-dark);
}

.hint {
    margin: 12px 0 0;
    text-align: center;
    font-size: 12px;
    color: var(--muted);
    line-height: 1.5;
    word-break: break-all;
}
</style>
